import React from 'react';

import { cn } from '@/lib/utils';
import type { ClientConversation } from '@/services/api/notificationsService';
import { stripMarkdownSyntax } from '@/utils/markdown';

interface MessageListItemProps {
  conversation: ClientConversation;
  isSelected: boolean;
  onSelect: () => void;
}

/**
 * Format a timestamp to relative time (e.g., "5m", "2h", "3d")
 */
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const MessageListItem: React.FC<MessageListItemProps> = ({
  conversation,
  isSelected,
  onSelect,
}) => {
  const hasUnread = conversation.unread_count > 0;

  // Display name: prefer client name, fall back to matter name
  const displayName = conversation.client_name || conversation.matter_name || 'Unknown';

  // Determine background style based on state
  const bgStyle = isSelected
    ? 'bg-indigo-50 border-l-indigo-600'
    : hasUnread
      ? 'bg-indigo-50 border-l-transparent hover:bg-gray-50'
      : 'bg-white border-l-transparent hover:bg-gray-50';

  return (
    <button
      type="button"
      onClick={onSelect}
      data-notification-item
      className={cn(
        'w-full text-left px-3 py-2.5 transition-colors border-l-2',
        bgStyle,
      )}
    >
      <div className="flex flex-col gap-0.5">
        {/* Top row: Name + time + unread indicator */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-bold text-gray-900 truncate">
            {displayName}
          </span>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-[8px] text-gray-500 font-semibold tabular-nums">
              {formatRelativeTime(conversation.latest_message_timestamp)}
            </span>
            {hasUnread && (
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-600 flex-shrink-0" />
            )}
          </div>
        </div>

        {/* Message preview */}
        <p
          className={cn(
            'text-[11px] leading-snug line-clamp-2 font-medium',
            hasUnread ? 'text-gray-700' : 'text-gray-600',
          )}
        >
          {stripMarkdownSyntax(conversation.latest_message_preview)}
        </p>
      </div>
    </button>
  );
};
